import {useCallback, useMemo} from "react";
import {useDispatch, useSelector} from "react-redux";
import Subscription from "./subscription";
import {setWorkspaceGraphSubscriptions, workspaceGraphSelectors} from "../../system/workspace-graph-slice";
import {normalizeMembershipEntity} from "../workspace-membership/workspace-membership-identity";

const ACTIVE_STATUSES = ["trialing", "active", "past_due", "paused"];

export function useSubscription() {
    const dispatch = useDispatch();
    const subscriptions = useSelector(workspaceGraphSelectors.subscriptions);
    const workspaceMembership = useSelector((state) => state.app?.workspaceMembershipData);
    const memberships = useMemo(
        () => workspaceMembership?.workspace?.workspace_memberships || [],
        [workspaceMembership],
    );

    const getCurrentSubscription = useCallback(async (workspaceId) => {
        if (!workspaceId) return {subscriptionData: null, subscriptionPlanData: null};

        const subscription = new Subscription();
        const response = await subscription.getAll({
            filters: {
                subscriptionStatus: {$in: [...ACTIVE_STATUSES, "canceled", "expired"]},
            },
            populate: {
                workspace: true,
                activatedBy: {populate: {member: {populate: {profileImage: true}}}},
                plan: {populate: {prices: true, marketingFeatures: true}},
                planPrice: true,
                addons: true,
            },
            sort: ["currentPeriodEnd:desc", "createdAt:desc"],
            pagination: {pageSize: 20},
        });
        const items = (response?.data || []).map((item) =>
            normalizeMembershipEntity(item, "subscription", memberships));
        dispatch(setWorkspaceGraphSubscriptions(items));
        const now = Date.now();
        const subscriptionData = items.find((item) => {
            if (!ACTIVE_STATUSES.includes(item.subscriptionStatus)) return false;
            const periodEnd = item.currentPeriodEnd || item.expireAt;
            if (!periodEnd) return true;
            const periodEndTime = new Date(periodEnd).getTime();
            return !Number.isNaN(periodEndTime) && periodEndTime >= now;
        }) || items[0] || null;
        const planData = subscriptionData?.plan || null;
        const expirationDate = subscriptionData?.currentPeriodEnd;
        const subscriptionPlanData = planData ? {
            ...planData,
            planPrice: subscriptionData?.planPrice || null,
            expireAt: expirationDate,
            isExpired: expirationDate ? new Date(expirationDate).getTime() < now : false,
        } : null;

        return {subscriptionData, subscriptionPlanData};
    }, [dispatch, memberships]);



    const getEntitlements = useCallback(async (documentId) => {
        if (!documentId) return [];
        const response = await new Subscription().getEntitlements(documentId);
        return response?.data || [];
    }, []);

    const renewSubscription = useCallback(async (documentId) => {
        const response = await new Subscription().renew(documentId);
        return response?.data || null;
    }, []);

    const changeSubscriptionPlan = useCallback(async (documentId, payload) => {
        const response = await new Subscription().changePlan(documentId, payload);
        return response?.data || null;
    }, []);

    const cancelSubscription = useCallback(async (documentId, immediate = false) => {
        const response = await new Subscription().cancel(documentId, immediate);
        return response?.data || null;
    }, []);

    return {
        subscriptions,
        getCurrentSubscription,
        getEntitlements,
        renewSubscription,
        changeSubscriptionPlan,
        cancelSubscription,
    };
}
